import React, { useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router';
import { CustomCheckbox } from 'components/CustomCheckbox';
import { tableContainerStyle } from 'components/tables/common';
import {
  getCompleteCellClass,
  getDataCellClass,
} from 'components/tables/common/simpleTableClasses';
import { formatCurrency } from 'utils';
import type { ScratchersTableProps, ScratcherGameRow } from '../../types';
import '../../styles/ScratchersTable.scss';

type ScratchersColumn = {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  headerAlign?: 'left' | 'center' | 'right';
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  cellClassName?: string;
  renderCell: (row: ScratcherGameRow) => React.ReactNode;
};

type ScratchersBackstockColumn = ScratchersColumn & {
  showCheckbox?: boolean;
  checkboxKey?: 'vending' | 'locker' | 'cashier';
};

const isNonZeroNumber = (value: number | null | undefined): value is number =>
  value != null && value !== 0;

const formatNumberWhenNonZero = (value: number | null | undefined) =>
  isNonZeroNumber(value) ? value.toLocaleString() : '-';

const formatCurrencyWhenNonZero = (value: number | null | undefined) =>
  isNonZeroNumber(value) ? formatCurrency(value) : '-';

const formatDisplayDate = (value: string | null | undefined) => {
  if (!value || value.toLowerCase() === 'na') {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

const renderPackMetric = (
  tickets: number | null | undefined,
  packs: number | null | undefined
) => {
  const packValue = formatNumberWhenNonZero(packs);
  const ticketValue = formatNumberWhenNonZero(tickets);

  if (packValue === '-' && ticketValue === '-') {
    return <span className="scratchers-pack-count">-</span>;
  }

  return (
    <div className="scratchers-pack-cell">
      {packValue !== '-' && <span className="scratchers-pack-count">{packValue}</span>}
      {ticketValue !== '-' && (
        <span className="scratchers-pack-label">{`${ticketValue} tickets`}</span>
      )}
    </div>
  );
};

const getScratchersHeaderClass = (props: {
  rowIdx: number | string;
  colIdx: number;
  totalRows: number;
  totalCols: number;
}) => getCompleteCellClass(props).replace('table-reconcile', 'table-header');

const getColumnCellStyles = (
  column: ScratchersColumn,
  textAlign: ScratchersColumn['align'] = column.headerAlign ?? column.align ?? 'left'
) => ({
  width: column.width,
  minWidth: column.minWidth,
  maxWidth: column.maxWidth,
  textAlign,
});

export default function ScratchersTable({
  data,
  isLoading,
  isError,
  error,
  showWeeklySales = false,
  showMissingColumns = false,
}: ScratchersTableProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [backstockFilters, setBackstockFilters] = useState({
    vending: false,
    locker: false,
    cashier: false,
  });

  const handleRowClick = (row: ScratcherGameRow) => {
    navigate(`/store/${id}/scratcher/${row.game_id}/packlifecycle`);
  };

  const handleBackstockFilterChange = (
    key: 'vending' | 'locker' | 'cashier'
  ) => (event: React.ChangeEvent<HTMLInputElement> | { target: { checked: boolean } }) => {
    setBackstockFilters((current) => ({
      ...current,
      [key]: event.target.checked,
    }));
  };

  const leadingColumns = useMemo<ScratchersColumn[]>(() => [
    {
      key: 'game_info',
      label: 'Game',
      align: 'left',
      headerAlign: 'left',
      width: '130px',
      minWidth: '130px',
      maxWidth: '130px',
      cellClassName: 'scratchers-table__cell--game',
      renderCell: (row) => (
        <div className="scratchers-game-info">
          <Link
            to={`/store/${id}/scratcher/${row.game_id}/packlifecycle`}
            className="scratchers-game-link"
            onClick={(event) => event.stopPropagation()}
          >
            {row.game_number ?? '-'}
          </Link>
          <span className="scratchers-game-upc">{row.upc ?? '-'}</span>
        </div>
      ),
    },
    {
      key: 'game_name',
      label: 'Name',
      align: 'left',
      headerAlign: 'left',
      minWidth: '360px',
      cellClassName: 'scratchers-table__cell--name',
      renderCell: (row) => {
        const startDate = formatDisplayDate(row.start_date);
        const endDate = formatDisplayDate(row.end_date);
        const dateRangeLabel = endDate !== '-' ? `${startDate} - ${endDate}` : startDate;

        return (
          <div className="scratchers-name-cell">
            <div className="scratchers-name-main">
              <span className="scratchers-name-primary">{row.game_name ?? '-'}</span>
              <span className="scratchers-name-secondary">
                {row.rate != null ? `| ${formatCurrency(row.rate)}` : '-'}
                {row.tickets_per_pack != null ? `, ${row.tickets_per_pack} tickets per pack` : ''}
              </span>
            </div>
            <div className="scratchers-name-meta">
              <span>{dateRangeLabel}</span>
            </div>
          </div>
        );
      },
    },
  ], [id]);

  const backstockColumns = useMemo<ScratchersBackstockColumn[]>(() => [
    {
      key: 'backstock_vending',
      label: 'Vending',
      align: 'right',
      headerAlign: 'right',
      width: '100px',
      minWidth: '100px',
      showCheckbox: true,
      checkboxKey: 'vending',
      renderCell: (row) =>
        renderPackMetric(row.backstock_vending_tickets, row.backstock_vending),
      },
    {
      key: 'backstock_locker',
      label: 'Locker',
      align: 'right',
      headerAlign: 'right',
      width: '100px',
      minWidth: '100px',
      showCheckbox: true,
      checkboxKey: 'locker',
      renderCell: (row) =>
        renderPackMetric(row.backstock_locker_tickets, row.backstock_locker),
      },
    {
      key: 'backstock_cashier',
      label: 'Cashier',
      align: 'right',
      headerAlign: 'right',
      width: '100px',
      minWidth: '100px',
      showCheckbox: true,
      checkboxKey: 'cashier',
      renderCell: (row) =>
        renderPackMetric(row.backstock_cashier_tickets, row.backstock_cashier),
    },
    {
      key: 'backstock_total',
      label: 'Total',
      align: 'right',
      headerAlign: 'right',
      width: '100px',
      minWidth: '100px',
      renderCell: (row) =>
        renderPackMetric(row.backstock_total_tickets, row.backstock_total),
    },
    {
      key: 'backstock_value',
      label: 'Value',
      align: 'right',
      headerAlign: 'right',
      width: '105px',
      maxWidth: '105px',
      renderCell: (row) =>
        <span>{formatCurrencyWhenNonZero(row.backstock_value)}</span>,
    },
  ], []);

  const trailingColumns = useMemo<ScratchersColumn[]>(() => {
    const columns: ScratchersColumn[] = [];

    if (showMissingColumns) {
      columns.push(
        {
          key: 'backstock_missing',
          label: 'Backstock Missing',
          align: 'right',
          headerAlign: 'right',
          width: '105px',
          maxWidth: '105px',
          renderCell: (row) =>
            renderPackMetric(row.backstock_missing_tickets, row.backstock_missing),
        },
        {
          key: 'backstock_missing_reconciled',
          label: 'Backstock Missing /Reconcile',
          align: 'right',
          headerAlign: 'right',
          width: '105px',
          maxWidth: '105px',
          renderCell: (row) =>
            renderPackMetric(
              row.backstock_missing_reconciled_tickets,
              row.backstock_missing_reconciled
            ),
        }
      );
    }

    columns.push(
      {
        key: 'on_counter_count',
        label: 'Activated (Counter)',
        align: 'right',
        headerAlign: 'right',
        width: '105px',
        maxWidth: '105px',
        renderCell: (row) =>
          renderPackMetric(row.activated_on_counter_tickets, row.on_counter_count),
      },
      {
        key: 'on_counter_value',
        label: 'Counter Value',
        align: 'right',
        headerAlign: 'right',
          width: '105px',
          maxWidth: '105px',
          renderCell: (row) =>
            <span>{formatCurrencyWhenNonZero(row.on_counter_value)}</span>,
      }
    );

    if (showWeeklySales) {
      columns.push(
        {
          key: 'weekly_sold',
          label: 'Weekly Sold',
          align: 'right',
          headerAlign: 'right',
          width: '85px',
          maxWidth: '85px',
          renderCell: (row) => <span>{formatNumberWhenNonZero(row.weekly_sold)}</span>,
        },
        {
          key: 'weekly_sales',
          label: 'Weekly Sales',
          align: 'right',
          headerAlign: 'right',
          width: '90px',
          maxWidth: '90px',
          renderCell: (row) =>
            <span>{formatCurrencyWhenNonZero(row.weekly_sales)}</span>,
        }
      );
    }

    columns.push({
      key: 'not_activated',
      label: 'Not Activated',
      align: 'right',
      headerAlign: 'right',
      width: '100px',
      maxWidth: '100px',
      renderCell: (row) => <span>{formatNumberWhenNonZero(row.not_activated)}</span>,
    });

    return columns;
  }, [showMissingColumns, showWeeklySales]);

  const columns = useMemo(
    () => [...leadingColumns, ...backstockColumns, ...trailingColumns],
    [leadingColumns, backstockColumns, trailingColumns]
  );

  const visibleRows = useMemo(() => {
    const activeFilters = Object.entries(backstockFilters).filter(([, checked]) => checked);
    if (activeFilters.length === 0) {
      return data;
    }

    return data.filter((row) =>
      activeFilters.some(([key]) => {
        if (key === 'vending') {
          return (row.backstock_vending ?? 0) !== 0 || (row.backstock_vending_tickets ?? 0) !== 0;
        }

        if (key === 'locker') {
          return (row.backstock_locker ?? 0) !== 0 || (row.backstock_locker_tickets ?? 0) !== 0;
        }

        return (row.backstock_cashier ?? 0) !== 0 || (row.backstock_cashier_tickets ?? 0) !== 0;
      })
    );
  }, [backstockFilters, data]);

  const totalColumns = columns.length;
  const backstockStartIndex = leadingColumns.length;
  const borderColumns = useMemo(() => {
    const boundaries: number[] = [0, 1];
    const backstockEndIndex = backstockStartIndex + backstockColumns.length - 1;

    boundaries.push(backstockEndIndex);

    for (let index = backstockEndIndex + 1; index < totalColumns - 1; index += 1) {
      boundaries.push(index);
    }

    return boundaries;
  }, [backstockColumns.length, backstockStartIndex, totalColumns]);

  return (
    <TableContainer
      component={Paper}
      className="scratchers-table-container"
      sx={{
        ...tableContainerStyle,
        marginTop: 0,
      }}
    >
      <Table size="small" stickyHeader className="scratchers-table">
        <TableHead>
          <TableRow>
            {leadingColumns.map((column, columnIndex) => (
              <TableCell
                key={column.key}
                rowSpan={2}
                className={getScratchersHeaderClass({
                  rowIdx: 0,
                  colIdx: columnIndex,
                  totalRows: 1,
                  totalCols: totalColumns,
                })}
                sx={getColumnCellStyles(column)}
              >
                <p
                  className="parent-header-text"
                  style={{
                    textAlign: column.headerAlign ?? column.align ?? 'left',
                    width: '100%',
                  }}
                >
                  {column.label}
                </p>
              </TableCell>
            ))}

            <TableCell
              colSpan={backstockColumns.length}
              className={`${getScratchersHeaderClass({
                rowIdx: 0,
                colIdx: backstockStartIndex,
                totalRows: 2,
                totalCols: totalColumns,
              })} scratchers-table__group-header`.trim()}
              sx={{ textAlign: 'center' }}
            >
              <p className="parent-header-text">Activated Backstock</p>
            </TableCell>

            {trailingColumns.map((column, trailingIndex) => {
              const columnIndex = backstockStartIndex + backstockColumns.length + trailingIndex;
              return (
                <TableCell
                  key={column.key}
                  rowSpan={2}
                  className={getScratchersHeaderClass({
                    rowIdx: 0,
                    colIdx: columnIndex,
                    totalRows: 1,
                    totalCols: totalColumns,
                  })}
                  sx={getColumnCellStyles(column)}
                >
                  <p
                    className="parent-header-text"
                    style={{
                      textAlign: column.headerAlign ?? column.align ?? 'left',
                      width: '100%',
                    }}
                  >
                    {column.label}
                  </p>
                </TableCell>
              );
            })}
          </TableRow>

          <TableRow>
            {backstockColumns.map((column, subIndex) => {
              const columnIndex = backstockStartIndex + subIndex;
              return (
                <TableCell
                  key={column.key}
                  className={`${getCompleteCellClass({
                    rowIdx: 'subheader',
                    colIdx: columnIndex,
                    totalRows: 2,
                    totalCols: totalColumns,
                    borderColumns,
                  })} scratchers-table__subheader-cell`.trim()}
                  sx={getColumnCellStyles(column, 'right')}
                >
                  <div
                    className="scratchers-table__subheader-content"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {column.showCheckbox && column.checkboxKey && (
                      <CustomCheckbox
                        checked={backstockFilters[column.checkboxKey]}
                        onChange={handleBackstockFilterChange(column.checkboxKey)}
                      />
                    )}
                    <span className="scratchers-table__subheader-label">{column.label}</span>
                  </div>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={totalColumns} sx={{ textAlign: 'center', padding: '40px 20px' }}>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  gap={2}
                >
                  <CircularProgress size={40} />
                  <p>Loading scratcher games...</p>
                </Box>
              </TableCell>
            </TableRow>
          ) : isError ? (
            <TableRow>
              <TableCell
                colSpan={totalColumns}
                className="scratchers-table__empty-state"
              >
                {error?.message || 'Unable to load scratcher games.'}
              </TableCell>
            </TableRow>
          ) : visibleRows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={totalColumns}
                className="scratchers-table__empty-state"
              >
                No scratcher games found for this store
              </TableCell>
            </TableRow>
          ) : (
            visibleRows.map((row, rowIndex) => (
              <TableRow
                key={`${row.game_id}-${row.upc ?? ''}`}
                className={
                  rowIndex % 2 === 0
                    ? 'scratchers-table__row scratchers-table__row--odd'
                    : 'scratchers-table__row scratchers-table__row--even'
                }
                onClick={() => handleRowClick(row)}
              >
                {columns.map((column, columnIndex) => (
                  <TableCell
                    key={`${row.game_id}-${column.key}`}
                    className={`${getDataCellClass(
                      columnIndex,
                      rowIndex,
                      visibleRows.length,
                      borderColumns,
                      totalColumns
                    )} ${column.cellClassName ?? ''}`.trim()}
                    sx={getColumnCellStyles(column, column.align ?? 'left')}
                  >
                    {column.renderCell(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
